<template>
  <!-- Acciones de una línea agrupadas en un solo icono. En pantallas grandes (más de `anchoMaximo`)
       el contenido del slot se pinta tal cual, como siempre; en tablet y móvil se pinta un único
       icono que despliega las acciones en un panel flotante al pasar el ratón, hacer click, tocar o
       mantener pulsado. El panel se saca al <body> al abrirse para que ninguna columna fija ni
       ningún z-index del listado lo tape. -->
  <span class="r-acciones-agrupadas" :class="{ 'r-acciones-agrupadas--agrupado': agrupadas }">
    <template v-if="agrupadas">
      <button
        v-if="!sinIcono"
        type="button"
        class="link icon-only acciones-trigger"
        :class="{ 'acciones-trigger-activo': abierto }"
        :aria-label="$t('common.acciones')"
        @mouseenter="ratonEntra"
        @mouseleave="ratonSale"
        @touchstart.passive="toqueInicio"
        @touchend="toqueFin"
        @touchmove.passive="toqueCancelar"
        @touchcancel.passive="toqueCancelar"
        @contextmenu.prevent
        @click.prevent.stop="clickTrigger"
      ><i class="icon f7-icons">{{ icono }}</i></button>
      <!-- El cierre al pulsar una acción va en CAPTURA: así se cierra aunque la acción corte la
           propagación (@click.stop), que si no el panel se quedaba abierto encima de la página a la
           que navega la acción (la ruta con keepAlive no destruye el componente). -->
      <div
        v-if="abierto"
        ref="panel"
        class="acciones-flotantes"
        :style="estilo"
        @mouseenter="cancelarCierre"
        @mouseleave="ratonSale"
        @click.capture="cerrar(60)"
      ><slot></slot></div>
    </template>
    <slot v-else></slot>
  </span>
</template>

<script>
import Ventana from "../js/Ventana.js";

export default {
  name: "rAccionesAgrupadas",
  props: {
    // Apagándolo se pintan siempre las acciones sueltas.
    agrupar: {
      type: Boolean,
      default: true
    },
    // Solo se agrupa con la ventana a este ancho o menos (mismo corte que el CSS responsive).
    anchoMaximo: {
      type: Number,
      default: 1024
    },
    icono: {
      type: String,
      default: "ellipsis_vertical_circle"
    },
    // Sin icono propio: quien lo usa abre el panel con `conmutar(rect)` (p. ej. al tocar la fila).
    sinIcono: {
      type: Boolean,
      default: false
    },
    // Selector del ancestro que hace de "ancla" (la fila): un toque dentro de él no cuenta como
    // toque fuera, porque es la propia fila la que abre y cierra el panel.
    ancla: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      abierto: false,
      estilo: {},
      timerCierre: null,
      timerToque: null,
      ultimoToque: 0,          // para ignorar los eventos de ratón emulados que siguen a un toque
      abiertoPorToque: false   // la pulsación larga ya abrió el panel: el touchend no debe cerrarlo
    };
  },
  computed: {
    agrupadas() {
      return this.agrupar && Ventana.ancho <= this.anchoMaximo;
    }
  },
  watch: {
    agrupadas(valor) {
      if (!valor) {
        this.cerrarAhora();
      }
    }
  },
  beforeDestroy() {
    this.cerrarAhora();
    this.toqueCancelar();
    // El panel abierto vive en el <body>: si la fila desaparece con él abierto (recarga de datos,
    // lista virtual), Vue quita la raíz del componente pero no el panel, que se quedaría flotando.
    const panel = this.$refs.panel;
    if (panel && panel.parentNode) {
      panel.remove();
    }
  },
  methods: {
    // Toque en la fila (modo sin icono): abre o cierra el panel anclado a esa fila, salvo que el toque
    // haya caído en algo que ya hace lo suyo (enlaces, botones, checkbox...). Devuelve si lo gestionó.
    toqueEnFila(event) {
      const t = event && event.target;
      if (t && t.closest && t.closest("a, button, input, label, .checkbox, .link, .r-acciones-agrupadas, .acciones-flotantes")) {
        return false;
      }
      this.conmutarEnFila(event.currentTarget);
      return true;
    },
    // Abre o cierra anclado a la fila: a su altura y pegado al borde derecho VISIBLE del contenedor que
    // hace scroll horizontal (la fila suele ser más ancha que la pantalla), sin contar la barra de scroll.
    conmutarEnFila(filaEl) {
      if (!filaEl || !filaEl.getBoundingClientRect) {
        return;
      }
      const fila = filaEl.getBoundingClientRect();
      let bordeDerecho = window.innerWidth;
      let el = filaEl.parentElement;
      while (el && el !== document.body) {
        const cs = getComputedStyle(el);
        if (/(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 1) {
          bordeDerecho = el.getBoundingClientRect().left + el.clientWidth;
          break;
        }
        el = el.parentElement;
      }
      this.conmutar({
        top: fila.top,
        height: fila.height,
        right: Math.min(bordeDerecho, window.innerWidth) - 4
      });
    },
    // Abre o cierra desde fuera (modo sin icono), anclado a un elemento o a un rectángulo {top, height, right}.
    conmutar(ancla) {
      if (this.abierto) {
        this.cerrarAhora();
      } else {
        this.abrir(ancla);
      }
    },
    abrir(el) {
      this.cancelarCierre();
      el = el || this.$el.querySelector(".acciones-trigger");
      if (!el) {
        return;
      }
      // Vale un elemento o directamente un rectángulo (viewport) con top, height y right.
      const r = el.getBoundingClientRect ? el.getBoundingClientRect() : el;
      if (!r || typeof r.top !== "number") {
        return;
      }
      // Pegado al icono por su derecha, a la altura de la línea; las acciones crecen hacia la izquierda.
      this.estilo = {
        top: `${Math.round(r.top + r.height / 2)}px`,
        right: `${Math.round(window.innerWidth - r.right)}px`
      };
      if (!this.abierto) {
        this.abierto = true;
        document.addEventListener("touchstart", this.fuera, { passive: true, capture: true });
        document.addEventListener("mousedown", this.fuera, true);
        document.addEventListener("scroll", this.alDesplazar, true);
        window.addEventListener("resize", this.cerrarAhora);
      }
      // El panel nace dentro de la celda; se lleva al body para que nada del listado lo tape.
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        if (panel && panel.parentNode !== document.body) {
          document.body.appendChild(panel);
        }
      });
    },
    cerrarAhora() {
      this.cancelarCierre();
      if (!this.abierto) {
        return;
      }
      this.abierto = false;
      document.removeEventListener("touchstart", this.fuera, { passive: true, capture: true });
      document.removeEventListener("mousedown", this.fuera, true);
      document.removeEventListener("scroll", this.alDesplazar, true);
      window.removeEventListener("resize", this.cerrarAhora);
    },
    // Desplazar el listado o la página cierra el panel (se quedaría flotando donde estaba la fila);
    // desplazar el PROPIO panel no: en pantallas estrechas las acciones que no caben se alcanzan
    // deslizándolo en horizontal.
    alDesplazar(event) {
      const panel = this.$refs.panel;
      const origen = event && event.target;
      if (panel && origen && origen.nodeType === 1 && panel.contains(origen)) {
        return;
      }
      this.cerrarAhora();
    },
    cerrar(retraso) {
      this.cancelarCierre();
      this.timerCierre = setTimeout(() => {
        this.timerCierre = null;
        this.cerrarAhora();
      }, retraso || 150);
    },
    cancelarCierre() {
      if (this.timerCierre) {
        clearTimeout(this.timerCierre);
        this.timerCierre = null;
      }
    },
    esToqueReciente() {
      return Date.now() - this.ultimoToque < 700;
    },
    ratonEntra(event) {
      // Tras un toque el navegador emula mouseenter/click: eso lo gestionan los manejadores táctiles.
      if (this.esToqueReciente()) {
        return;
      }
      this.abrir(event.currentTarget);
    },
    ratonSale() {
      if (this.esToqueReciente()) {
        return;
      }
      this.cerrar();
    },
    clickTrigger(event) {
      if (this.esToqueReciente()) {
        return;
      }
      if (this.abierto) {
        this.cerrarAhora();
      } else {
        this.abrir(event.currentTarget);
      }
    },
    toqueInicio(event) {
      const el = event.currentTarget;
      this.ultimoToque = Date.now();
      this.abiertoPorToque = false;
      this.toqueCancelar();
      // Pulsación larga: se abre sin soltar.
      this.timerToque = setTimeout(() => {
        this.timerToque = null;
        this.abiertoPorToque = true;
        this.abrir(el);
      }, 400);
    },
    toqueCancelar() {
      if (this.timerToque) {
        clearTimeout(this.timerToque);
        this.timerToque = null;
      }
    },
    toqueFin(event) {
      this.ultimoToque = Date.now();
      // Sin el preventDefault el navegador dispararía además el click emulado.
      if (event.cancelable) {
        event.preventDefault();
      }
      if (this.abiertoPorToque) {
        // Lo abrió la pulsación larga: se queda abierto hasta tocar una acción o fuera.
        this.abiertoPorToque = false;
        return;
      }
      this.toqueCancelar();
      // Un toque corto también conmuta el panel: en la tablet es lo más rápido.
      if (this.abierto) {
        this.cerrarAhora();
      } else {
        this.abrir(event.currentTarget);
      }
    },
    fuera(event) {
      if (!this.abierto) {
        return;
      }
      const t = event.target;
      const panel = this.$refs.panel;
      if (t && ((panel && panel.contains(t)) || this.$el.contains(t))) {
        return;
      }
      // Con ancla (la fila que abre el panel al tocarla), el toque en la fila lo gestiona ella.
      if (t && this.ancla && this.$el.closest) {
        const fila = this.$el.closest(this.ancla);
        if (fila && fila.contains(t)) {
          return;
        }
      }
      this.cerrarAhora();
    }
  }
};
</script>

<style scoped>
.r-acciones-agrupadas {
  /* Sin agrupar no debe alterar el layout de quien lo usa (celdas flex, etc.). */
  display: contents;
}
.r-acciones-agrupadas--agrupado {
  display: inline-flex;
  align-items: center;
}
.acciones-trigger {
  /* Es un <button> (accesible por teclado) con aspecto de icono: fuera el estilo del navegador y el
     width:100% que fuerza vuetify.css en los button. */
  border: 0;
  background: none;
  padding: 0;
  margin: 0;
  font: inherit;
  width: 32px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  vertical-align: middle;
  cursor: pointer;
  color: var(--f7-theme-color, #007aff);
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}
.acciones-trigger .icon {
  font-size: 22px;
}
.acciones-trigger-activo {
  color: #ff9500;
}
.acciones-flotantes {
  position: fixed;
  z-index: 20000;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  padding: 2px 8px;
  background: var(--f7-page-bg-color, #fff);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
  line-height: 1;
  max-width: calc(100vw - 16px);
  overflow-x: auto;
}
/* Las acciones las pinta la pantalla (su propio scope), de ahí el ::v-deep. */
.acciones-flotantes ::v-deep a.link {
  margin: 0 5px;
}
.acciones-flotantes ::v-deep a.link .icon {
  font-size: 22px;
}
.acciones-flotantes ::v-deep .button {
  margin: 0 3px;
}
</style>
